import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Alert,
  Box,
  Button
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Html5Qrcode } from 'html5-qrcode';

/**
 * CameraScannerDialog - Camera-based QR/barcode scanner using native video
 * Based on BarcodeScannerNative which works reliably on mobile
 */
export default function CameraScannerDialog({ open, onScan, onClose }) {
  const [hasPermission, setHasPermission] = useState(false);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const lastScanRef = useRef(null);
  const lastScanTimeRef = useRef(0);

  // Detect iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  // Comprehensive cleanup function
  const cleanup = () => {
    try {
      console.log('Camera cleanup started');

      // Clear scanning interval
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }

      // Stop camera stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => {
          track.stop();
          console.log('Camera track stopped:', track.label);
        });
        streamRef.current = null;
      }

      // Clear video element
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }

      // Clear HTML5 QR code instance
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
      }

      // Reset state
      setScanning(false);
      setCameraReady(false);
      setError(null);

      console.log('Camera cleanup complete');
    } catch (err) {
      console.error('Error during cleanup:', err);
    }
  };

  // Cleanup on unmount or close
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []);

  // Start camera when dialog opens
  const startCamera = async () => {
    try {
      console.log('Starting camera scanner...');
      setError(null);

      // Request camera with mobile-friendly constraints
      const constraints = {
        video: {
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };

      console.log('Getting camera stream...');
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      console.log('Camera stream obtained');

      // Set video source
      if (videoRef.current) {
        console.log('Setting video source');
        videoRef.current.srcObject = stream;

        // Important: Set these attributes for iOS Safari
        videoRef.current.setAttribute('autoplay', 'true');
        videoRef.current.setAttribute('muted', 'true');
        videoRef.current.setAttribute('playsinline', 'true');

        // Wait for video to be ready
        await new Promise((resolve) => {
          videoRef.current.onloadedmetadata = () => {
            resolve();
          };
          // Also resolve after a delay to prevent hanging
          setTimeout(resolve, 2000);
        });

        // Play the video
        console.log('Playing video...');
        await videoRef.current.play();
        console.log('Video playing successfully');

        setHasPermission(true);
        setScanning(true);
        setCameraReady(true);
        console.log('Camera ready, starting scanning');

        // Start scanning
        startScanning();
      }
    } catch (err) {
      console.log('Camera error:', err.name, err.message);

      // Detailed error messages
      let errorMessage = '';

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage = 'Camera permission denied.\n\nOn iOS:\n1. Go to Settings > Safari > Camera\n2. Set to "Allow"\n3. Refresh this page and try again';
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'No camera found on this device.';
      } else if (err.name === 'NotReadableError') {
        errorMessage = 'Camera is already in use. Please close other camera apps and try again.';
      } else {
        errorMessage = `Camera error: ${err.message}\n\nPlease use the keyboard input with your scanner instead.`;
      }

      setError(errorMessage);
      setScanning(false);
      setCameraReady(false);
    }
  };

  const startScanning = () => {
    if (!videoRef.current) return;

    console.log('Starting scanning logic');
    // Create Html5Qrcode instance with explicit format support
    const html5QrCode = new Html5Qrcode("dummy-element", {
      // Enable all common barcode formats
      formatsToSupport: [
        0,  // QR_CODE
        1,  // AZTEC
        2,  // CODABAR
        3,  // CODE_39
        4,  // CODE_93
        5,  // CODE_128
        6,  // DATA_MATRIX
        7,  // MAXICODE
        8,  // ITF
        9,  // EAN_13
        10, // EAN_8
        11, // PDF_417
        12, // RSS_14
        13, // RSS_EXPANDED
        14, // UPC_A
        15, // UPC_E
        16  // UPC_EAN_EXTENSION
      ],
      verbose: false
    });
    html5QrCodeRef.current = html5QrCode;

    // Scan every 300ms for faster detection
    console.log('Setting up scan interval');
    scanIntervalRef.current = setInterval(async () => {
      if (!videoRef.current || videoRef.current.paused || videoRef.current.ended) {
        return;
      }

      try {
        // Create canvas to capture frame
        const canvas = document.createElement('canvas');
        const fullWidth = videoRef.current.videoWidth;
        const fullHeight = videoRef.current.videoHeight;

        // Use a larger region for better QR code detection
        const cropSize = Math.min(fullWidth * 0.85, fullHeight * 0.85, 450); // Larger region, max 450px
        const cropWidth = cropSize;
        const cropHeight = cropSize;
        const cropX = (fullWidth - cropWidth) / 2;
        const cropY = (fullHeight - cropHeight) / 2;

        canvas.width = cropWidth;
        canvas.height = cropHeight;
        const ctx = canvas.getContext('2d');

        // Draw the cropped region
        ctx.drawImage(
          videoRef.current,
          cropX, cropY, cropWidth, cropHeight, // Source crop
          0, 0, cropWidth, cropHeight          // Destination
        );

        // Softer contrast enhancement - don't make it pure black/white
        const imageData = ctx.getImageData(0, 0, cropWidth, cropHeight);
        const data = imageData.data;

        // Gentler contrast enhancement for QR codes
        for (let i = 0; i < data.length; i += 4) {
          // Convert to grayscale
          const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
          // Softer threshold - enhance but don't completely binarize
          const enhanced = gray < 100 ? Math.max(0, gray - 30) : (gray > 155 ? Math.min(255, gray + 30) : gray);
          data[i] = enhanced;     // R
          data[i + 1] = enhanced; // G
          data[i + 2] = enhanced; // B
        }

        ctx.putImageData(imageData, 0, 0);

        // Convert to blob
        canvas.toBlob(async (blob) => {
          if (!blob) return;

          try {
            const file = new File([blob], "frame.png", { type: "image/png" });
            const result = await html5QrCode.scanFile(file, false);

            if (result) {
              console.log('Barcode detected!', result);

              // Extract equipment_id from QR code URL if it's a URL
              let scanValue = result;
              try {
                // Check if result is a URL
                if (result.startsWith('http://') || result.startsWith('https://')) {
                  const url = new URL(result);
                  const equipmentId = url.searchParams.get('equipment_id');
                  if (equipmentId) {
                    // Use equipment_id instead of full URL
                    scanValue = equipmentId;
                    console.log('Extracted equipment_id from QR URL:', equipmentId);
                  }
                }
              } catch (err) {
                // Not a URL or parsing failed, use original result
                console.log('Not a URL or failed to parse, using raw value:', result);
              }

              // Prevent duplicate scans of the same barcode within 3 seconds
              const now = Date.now();
              const timeSinceLastScan = now - lastScanTimeRef.current;

              if (lastScanRef.current === scanValue && timeSinceLastScan < 3000) {
                // Same barcode scanned within 3 seconds, ignore
                return;
              }

              // Update last scan tracking
              lastScanRef.current = scanValue;
              lastScanTimeRef.current = now;

              // Call parent's scan handler with extracted equipment_id
              onScan(scanValue);

              // Close dialog after successful scan
              handleClose();
            }
          } catch (err) {
            // Errors are normal when no barcode is in view
          }
        }, 'image/png');
      } catch (err) {
        // Ignore frame capture errors
      }
    }, 300);
  };

  const handleClose = async () => {
    cleanup();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: 'background.default'
        }
      }}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            Scan Barcode or QR Code
          </Typography>
        </Toolbar>
      </AppBar>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#000',
          overflow: 'hidden'
        }}
      >
        {/* Error state */}
        {error && (
          <Box sx={{ p: 3, maxWidth: 500 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Alert severity="info">
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Alternative: Use Keyboard Input
              </Typography>
              <Typography variant="body2">
                Close this dialog and use the text input field with your Zebra scanner or manually type the barcode.
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Camera view */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            backgroundColor: '#000'
          }}
        >
          <video
            ref={videoRef}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: hasPermission ? 'block' : 'none'
            }}
            autoPlay
            muted
            playsInline
          />

          {/* Show start button when camera isn't active */}
          {!cameraReady && !error && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                zIndex: 10
              }}
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={startCamera}
                sx={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  },
                  fontSize: '18px',
                  padding: '12px 32px'
                }}
              >
                Start Camera
              </Button>
              {isIOS && (
                <Box sx={{
                  mt: 2,
                  px: 2,
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  borderRadius: 1,
                  py: 1,
                  maxWidth: '300px'
                }}>
                  <Typography variant="body2" sx={{ textAlign: 'center', color: '#333' }}>
                    Tap "Start Camera" then "Allow" when prompted
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {scanning && cameraReady && (
            <>
              {/* Overlay with cutout */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.3)',
                  pointerEvents: 'none'
                }}
              />
              {/* Scan box - Square for QR codes and barcodes */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '45%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '250px',
                  height: '250px',
                  border: '3px solid #00ff00',
                  borderRadius: '12px',
                  backgroundColor: 'transparent',
                  boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -3,
                    left: -3,
                    right: -3,
                    bottom: -3,
                    border: '3px solid rgba(0,255,0,0.3)',
                    borderRadius: '12px'
                  }
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  position: 'absolute',
                  bottom: '20%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: 'white',
                  textAlign: 'center',
                  textShadow: '0 1px 3px rgba(0,0,0,0.8)'
                }}
              >
                {'Align QR code or barcode within green box'}
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>

      {/* Hidden element for Html5Qrcode */}
      <div id="dummy-element" style={{ display: 'none' }} />
    </Dialog>
  );
}
